import { cn } from '@fruuit/ui'
import { Link, useLocation } from 'react-router'
import type { AreaId, Me } from '../api/types'
import { AREAS } from '../areas/areas'
import type { NavItem, NavSection } from '../areas/types'
import { useSession } from '../auth/session-context'

function isActive(item: NavItem, pathname: string): boolean {
  const matches = (prefix: string) => pathname === prefix || pathname.startsWith(`${prefix}/`)
  if (item.end) return pathname === item.to
  return matches(item.to) || (item.activeFor ?? []).some(matches)
}

/** Drop entries the user's platform role doesn't unlock (hiding is cosmetic; the server decides). */
export function visibleNav(sections: NavSection[], me: Me): NavSection[] {
  return sections
    .map((section) => ({
      ...section,
      items: section.items.filter(
        (item) => item.requires === undefined || me.platform_role === item.requires,
      ),
    }))
    .filter((section) => section.items.length > 0)
}

/** Area navigation (NAV-*): kicker, grouped entries, active entry marked `aria-current="page"`. */
export function SideNav({ area, className }: { area: AreaId; className?: string }) {
  const { pathname } = useLocation()
  const me = useSession()
  const definition = AREAS[area]
  const sections = visibleNav(definition.nav, me)

  return (
    <nav aria-label={`${definition.label} navigation`} className={cn('flex flex-col', className)}>
      <p className="px-3 text-xs font-semibold tracking-[var(--tracking-kicker)] text-teal-500 uppercase">
        {definition.kicker}
      </p>
      {sections.map((section, index) => (
        <div key={section.label ?? index} className={index === 0 ? 'mt-3' : 'mt-6'}>
          {section.label ? (
            <p className="mb-1.5 px-3 text-xs font-semibold tracking-[0.1em] text-sage uppercase">
              {section.label}
            </p>
          ) : null}
          <ul className="flex flex-col gap-0.5">
            {section.items.map((item) => {
              const active = isActive(item, pathname)
              const Icon = item.icon
              return (
                <li key={item.to}>
                  <Link
                    to={item.to}
                    aria-current={active ? 'page' : undefined}
                    className={cn(
                      'group flex items-center gap-3 rounded-md px-3 py-2.5 text-sm no-underline transition-colors',
                      active
                        ? 'bg-teal font-semibold text-white shadow-[0_4px_10px_rgb(22_62_58/0.14)]'
                        : 'font-medium text-teal hover:bg-wash-strong',
                    )}
                  >
                    <Icon
                      aria-hidden="true"
                      className={cn(
                        'size-[18px] shrink-0',
                        active ? 'text-white' : 'text-teal-500 group-hover:text-teal',
                      )}
                    />
                    {item.label}
                  </Link>
                </li>
              )
            })}
          </ul>
        </div>
      ))}
    </nav>
  )
}
